import { t } from "@lingui/macro"
import { observer } from "mobx-react"
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useLocation, useNavigate, useParams } from "react-router-dom"
import { Box, InputAdornment, TextField } from "@mui/material"
import SearchIcon from "@mui/icons-material/Search"
import { debounce } from "lodash"

import {
    GridRowParams,
    GridLogicOperator,
    GridFilterModel,
} from "@mui/x-data-grid-pro"

import { AsyncRequestItemsStore, IAsyncRequestItemListItem } from "./store"
import { useGetAsyncRequestItemsColumns } from "./hooks/useGetAsyncRequestItemsColumns"
import useRouteParams from "./hooks/useRouteParams"

import { ListPage } from "src/components/ListPage"
import { DataGridProTable } from "src/components/Table/DataGridPro"
import { useInitializer } from "src/lib/initializer"
import { StoreProvider } from "src/store/lib/StoreProvider"
import { useStore } from "src/store/lib/useStore"
import { GlobalStore } from "src/store"
import { AsyncRequestItemDetailModal } from "src/modals/async-request-item-detail"

import {
    getFilterModelForRepository,
    getSortModelForRepository,
} from "src/lib/data-grid-pro"
import { DEFAULT_ACCESS_GROUP } from "src/config"
import {
    IAdvancedOperations,
    FilterModel,
    SortModel,
} from "src/types/data-grid-pro"
import { Repository } from "src/types/channel"

const repository: Repository = "async-request-item"

const View = observer(() => {
    const store = useStore(AsyncRequestItemsStore)
    const globalStore = useStore(GlobalStore)
    const _location = useLocation()
    const navigate = useNavigate()
    const { id } = useParams<{ id: string }>()
    const columns = useGetAsyncRequestItemsColumns()
    const allParams = useRouteParams()
    const [payloadSearch, setPayloadSearch] = useState(() => {
        // Initialize search from URL parameter
        const searchParam = allParams.find((param) => param.param === "search")
        return searchParam?.value ?? ""
    })

    const advancedOperationsModel: IAdvancedOperations = useMemo(
        () => ({
            pagination: "server",
            filtering: "server",
            sorting: "server",
        }),
        [],
    )

    const getDataGridInitialFilteringState = useCallback(():
        | GridFilterModel
        | undefined => {
        // Filter out search param since it's handled separately
        const filterParams = allParams.filter(
            (param) => param.param !== "search",
        )
        if (filterParams.length === 0) return undefined

        const params = store.handleParams(columns, filterParams)
        if (params?.length === 0) return undefined

        return { items: params }
    }, [columns, allParams, store])

    const advanceQuery = useMemo(() => {
        const filter = getFilterModelForRepository(
            repository,
            globalStore.session.dataGridFilterModel,
        )
        const sort = getSortModelForRepository(
            repository,
            globalStore.session.dataGridSortModel,
        )
        return { sort, filter }
    }, [
        globalStore.session.dataGridFilterModel,
        globalStore.session.dataGridSortModel,
    ])

    const initialized = useInitializer(async () => {
        const accessGroupId =
            globalStore.session.accessGroupId ?? DEFAULT_ACCESS_GROUP.id
        await store.init(accessGroupId, advanceQuery)
    }, [globalStore.session.accessGroupId, advanceQuery])

    const openAsyncRequestItemModal = useCallback(
        (itemId: number) => {
            globalStore.modals.open(
                () => (
                    <AsyncRequestItemDetailModal
                        id={itemId}
                        onClose={() => navigate("/async-request-items")}
                    />
                ),
                {
                    onClose: () => navigate("/async-request-items"),
                },
            )
        },
        [globalStore.modals, navigate],
    )

    useEffect(() => {
        if (!initialized) return
        if (id != null && !isNaN(Number(id))) {
            openAsyncRequestItemModal(Number(id))
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized, id])

    // Trigger search when component loads with URL parameters
    useEffect(() => {
        if (!initialized || payloadSearch.trim() === "") return

        // Trigger search if we have a search parameter from URL
        void debouncedPayloadSearch(payloadSearch)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [initialized, payloadSearch])

    const handleAsyncRequestItemClick = useCallback(
        (item: IAsyncRequestItemListItem) => {
            navigate(`/async-request-items/${item.id}`)
        },
        [navigate],
    )

    const handleFilterChange = useCallback(
        async (model: FilterModel) => {
            const sort = getSortModelForRepository(
                repository,
                globalStore.session.dataGridSortModel,
            )

            // Update URL with filter parameters
            const params = new URLSearchParams()

            // Preserve search parameter if it exists
            if (payloadSearch.trim() !== "") {
                params.set("search", payloadSearch)
            }

            // Add grid filter parameters
            model.items.forEach((item) => {
                if (item.value !== undefined) {
                    params.set(item.field, String(item.value))
                }
            })

            const url =
                params.toString() !== ""
                    ? `/async-request-items?${params.toString()}`
                    : "/async-request-items"
            navigate(url, { replace: true })

            await store.query({ sort, filter: model })
        },
        [globalStore.session.dataGridSortModel, store, navigate, payloadSearch],
    )

    const handleSortChange = useCallback(
        async (model: SortModel) => {
            const filter = getFilterModelForRepository(
                repository,
                globalStore.session.dataGridFilterModel,
            )
            await store.query({ filter, sort: model })
        },
        [globalStore.session.dataGridFilterModel, store],
    )

    const performPayloadSearch = useCallback(
        async (searchValue: string) => {
            // Get current filter and sort state
            const baseFilter = getFilterModelForRepository(
                repository,
                globalStore.session.dataGridFilterModel,
            )
            const sort = getSortModelForRepository(
                repository,
                globalStore.session.dataGridSortModel,
            )

            // Create payload filter
            const payloadFilter =
                searchValue.trim() !== ""
                    ? [
                          {
                              field: "payload",
                              operator: "contains",
                              value: searchValue.trim(),
                          },
                      ]
                    : []

            // Combine existing filters with payload filter
            const combinedFilter = {
                items: [...(baseFilter?.items ?? []), ...payloadFilter],
                logicOperator: GridLogicOperator.And,
            }

            await store.query({
                filter: combinedFilter,
                sort,
            })
        },
        [
            globalStore.session.dataGridFilterModel,
            globalStore.session.dataGridSortModel,
            store,
        ],
    )

    const debouncedPayloadSearch = useMemo(
        () => debounce(performPayloadSearch, 300),
        [performPayloadSearch],
    )

    const handlePayloadSearchChange = useCallback(
        (searchValue: string) => {
            setPayloadSearch(searchValue)

            // Update URL with search parameter
            const params = new URLSearchParams()

            // Add search parameter if it has value
            if (searchValue.trim() !== "") {
                params.set("search", searchValue.trim())
            }

            // Preserve existing grid filter parameters
            allParams.forEach((param) => {
                if (param.param !== "search") {
                    params.set(param.param, param.value)
                }
            })

            const url =
                params.toString() !== ""
                    ? `/async-request-items?${params.toString()}`
                    : "/async-request-items"
            navigate(url, { replace: true })

            void debouncedPayloadSearch(searchValue)
        },
        [debouncedPayloadSearch, navigate, allParams],
    )

    useEffect(() => {
        return () => {
            store.dispose()
            debouncedPayloadSearch.cancel()
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [debouncedPayloadSearch])

    const header = useMemo(
        () => ({
            header: t`async-request-items.header`,
            breadcrumbs: [t`async-request-items.breadcrumb`],
        }),
        [],
    )

    return (
        <ListPage header={header} loading={!initialized}>
            {/* Payload Search Bar */}
            <Box
                sx={{
                    mb: 3,
                    p: 3,
                    backgroundColor: "background.paper",
                    borderRadius: 2,
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                }}
            >
                <TextField
                    fullWidth
                    value={payloadSearch}
                    onChange={(e) => handlePayloadSearchChange(e.target.value)}
                    placeholder={t`async-request-items.search.placeholder`}
                    variant="outlined"
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon color="action" />
                            </InputAdornment>
                        ),
                        sx: {
                            fontSize: "1.1rem",
                            "& .MuiOutlinedInput-root": {
                                "&:hover fieldset": {
                                    borderColor: "primary.main",
                                },
                                "&.Mui-focused fieldset": {
                                    borderWidth: "2px",
                                },
                            },
                        },
                    }}
                    sx={{
                        "& .MuiInputBase-root": {
                            height: "56px",
                            backgroundColor: "background.default",
                            borderRadius: 2,
                        },
                        "& .MuiInputBase-input": {
                            fontSize: "1.1rem",
                            padding: "16px 14px",
                        },
                        "& .MuiInputLabel-root": {
                            fontSize: "1.1rem",
                        },
                    }}
                />
            </Box>

            <DataGridProTable
                paginator={store.asyncRequestItems}
                data={store.asyncRequestItems.items}
                columns={columns}
                advancedOperations={advancedOperationsModel}
                repository={repository}
                loading={!initialized}
                onFilterChange={handleFilterChange}
                onSortChange={handleSortChange}
                onRowClickEvent={(params: GridRowParams) =>
                    handleAsyncRequestItemClick(
                        params.row as IAsyncRequestItemListItem,
                    )
                }
                filteringInitialState={getDataGridInitialFilteringState()}
            />
        </ListPage>
    )
})

export const AsyncRequestItemsView = () => (
    <StoreProvider Store={AsyncRequestItemsStore}>
        <View />
    </StoreProvider>
)
